import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Factory,
  Cog,
  Wrench,
  Building2,
  Award,
  Users,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import anniversary50 from "../assets/50_years.png";
import cuttingTool from "../assets/cutting_tool_engineering.png";
import oilfield from "../assets/oilfield-industry.jpeg";
import homehero from '../assets/homehero.jpg';
import newcrowtomball from '../assets/new_crow_tomball.jpg';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroSlides = [
    {
      image:homehero,
      title: "Precision Manufacturing Excellence",
      subtitle: "64 Years of Manufacturing Solutions",
    },
    {
      image:
        "https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=1920&q=80",
      title: "State-of-the-Art Facility",
      subtitle: "60,000 Sq Ft • Advanced Manufacturing Capabilities",
    },
    {
      image:oilfield,
      title: "Turnkey Solutions",
      subtitle: "From Design to Delivery • Global Resources",
    },
    {
      image:
        "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=1920&q=80",
      title: "Quality You Can Trust",
      subtitle: "ISO Certified • Windlass Group Standards",
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + heroSlides.length) % heroSlides.length,
    );
  };

  const services = [
    {
      icon: Factory,
      title: "Metal Fabrication & Welding",
      description:
        "Expert structural welding and custom metal fabrication for industrial applications.",
      link: "/services/metal-fabrication",
    },
    {
      icon: Cog,
      title: "CNC Precision Machining",
      description:
        "State-of-the-art CNC machining for tight tolerance precision components.",
      link: "/services/cnc-machining",
    },
    {
      icon: Wrench,
      title: "Turnkey Manufacturing",
      description: "Complete manufacturing solutions from design to delivery.",
      link: "/services/turnkey-manufacturing",
    },
    {
      icon: Building2,
      title: "Warehousing & Supply Chain",
      description:
        "Comprehensive logistics and inventory management solutions.",
      link: "/services/warehousing",
    },
  ];

  const industries = [
    { name: "Oilfield", link: "/industries/oilfield" },
    { name: "Midstream & Process", link: "/industries/midstream" },
    { name: "Data Centers", link: "/industries/data-centers" },
    { name: "Infrastructure", link: "/industries/infrastructure" },
  ];

  const stats = [
    { number: "55+", label: "Years Experience" },
    { number: "60K", label: "Sq Ft Facility" },
    { number: "24/7", label: "Support Available" },
    { number: "100%", label: "Quality Assured" },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <section
        className="relative h-[600px] pt-32 overflow-hidden"
        data-testid="hero-section"
      >
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
         <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-900/70 to-transparent" />
          </div>
        ))}

        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-3xl text-white">
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl font-bold heading-font mb-6 leading-tight pt-8"
                data-testid="hero-heading"
              >
                {heroSlides[currentSlide].title}
              </h1>
              <p className="text-xl sm:text-2xl mb-8 text-blue-100">
                {heroSlides[currentSlide].subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-900 rounded-lg hover:bg-blue-50 font-semibold text-base transition-all shadow-lg hover:shadow-xl"
                  data-testid="hero-cta-quote"
                >
                  Request a Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white rounded-lg hover:bg-white/10 font-semibold text-base transition-all border-2 border-white"
                  data-testid="hero-cta-services"
                >
                  View Services
                </Link>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all"
          data-testid="hero-prev-button"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all"
          data-testid="hero-next-button"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? "bg-white w-8" : "bg-white/50"
              }`}
              data-testid={`hero-indicator-${index}`}
            />
          ))}
        </div>
      </section>

      <section
        className="py-12 px-4 sm:px-6 lg:px-8 bg-white -mt-16 relative z-10"
        data-testid="stats-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
                data-testid={`stat-${index}`}
              >
                <div className="text-3xl font-bold text-blue-700 heading-font mb-1">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="py-20 px-4 sm:px-6 lg:px-8 bg-white"
        data-testid="about-preview-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2
                className="text-3xl sm:text-4xl font-bold text-gray-900 heading-font mb-6"
                data-testid="about-preview-heading"
              >
                A Full-Service Contract Manufacturer
              </h2>
              <p className="text-gray-600 text-base mb-6 leading-relaxed">
                With over 55 years of experience, Crow Corporation has built a
                reputation for delivering precision metalworking solutions that
                meet the highest standards of quality and reliability.
              </p>
              <p className="text-gray-600 text-base mb-6 leading-relaxed">
                Our 60,000 square foot facility houses cutting-edge technology
                and a team of skilled professionals dedicated to bringing your
                vision to life—from prototype to production.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">
                    Multi-million dollar investment in latest technology
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">
                    Short run to high volume production capabilities
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">
                    Serving diverse industries nationwide
                  </span>
                </li>
              </ul>
              <Link
                to="/about"
                className="inline-flex items-center text-blue-700 hover:text-blue-800 font-semibold"
                data-testid="about-preview-link"
              >
                Learn More About Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={newcrowtomball}
                  alt="Manufacturing facility"
                  className="w-full h-full object-cover"
                  data-testid="about-preview-image"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-blue-700 text-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center space-x-3">
                  <Award className="w-8 h-8" />
                  <div>
                    <div className="text-2xl font-bold heading-font">ISO</div>
                    <div className="text-sm">Certified</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-[#1d4ed8] text-white py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
          {/* LEFT SIDE */}
          <div>
            <div className="flex items-center space-x-4 mb-6">
              <img
                src={cuttingTool}
                alt="Cutting Tool Engineering"
                className="w-40"
              />

              <div>
                <h2 className="text-2xl font-bold text-blue-400 tracking-wide">
                  ARTICLES
                </h2>
                <p className="text-sm italic text-gray-200">
                  Keith Jennings, President Crow Corp.
                </p>
              </div>
            </div>

            <ul className="space-y-4 mt-8">
              {[
                "Protecting digital assets",
                "Discounts and increases",
                "Adapting to today’s customer",
                "Cultivating the future of machining",
                "IMTS was a worthy experience",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-center border-b border-blue-400/30 pb-3"
                >
                  <span className="w-3 h-3 border-2 border-blue-400 rounded-full mr-4"></span>
                  <span className="text-gray-200">{item}</span>
                </li>
              ))}
            </ul>
          </div>

       {/* RIGHT SIDE */}
<div className="border-l border-white/30 pl-12 flex flex-col items-center text-center">

  <p className="text-blue-300 italic text-xl mb-3">
    "Money is applause."
  </p>

  <p className="text-gray-300 mb-8">
    - Jacqueline Susann
  </p>

  {/* 50 YEARS IMAGE */}
  <img
    src={anniversary50}
    alt="50 Years"
    className="w-72 opacity-40"
  />

</div>
        </div>
      </section>
      <section
        className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50"
        data-testid="services-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-bold text-gray-900 heading-font mb-4"
              data-testid="services-heading"
            >
              Our Core Services
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Comprehensive manufacturing solutions backed by decades of
              expertise and state-of-the-art equipment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="bg-white rounded-xl p-6 shadow-md card-hover group"
                data-testid={`service-card-${index}`}
              >
                <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-700 transition-colors">
                  <service.icon className="w-7 h-7 text-blue-700 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 heading-font">
                  {service.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  {service.description}
                </p>
                <div className="flex items-center text-blue-700 font-medium text-sm group-hover:text-blue-800">
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center px-6 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-semibold transition-all shadow-md"
              data-testid="view-all-services-button"
            >
              View All Services
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <section
        className="py-20 px-4 sm:px-6 lg:px-8 bg-white"
        data-testid="industries-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-3xl sm:text-4xl font-bold text-gray-900 heading-font mb-4"
              data-testid="industries-heading"
            >
              Industries We Serve
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Trusted by leading companies across multiple sectors.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <Link
                key={index}
                to={industry.link}
                className="group relative overflow-hidden rounded-xl aspect-[4/3] shadow-lg"
                data-testid={`industry-card-${index}`}
              >
                <img
                  src={`https://images.unsplash.com/photo-${
                    [
                      "1581091226825-a6a2a5aee158",
                      "1581092160607-ee22621dd758",
                      "1558494949-ef010cbdcc31",
                      "1503387762-592deb58ef4e",
                    ][index]
                  }?w=600&q=80`}
                  alt={industry.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent flex items-end p-6">
                  <div>
                    <h3 className="text-xl font-bold text-white heading-font mb-2">
                      {industry.name}
                    </h3>
                    <div className="flex items-center text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                      Explore
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section
        className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-700 to-blue-900 text-white"
        data-testid="cta-section"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold heading-font mb-6"
            data-testid="cta-heading"
          >
            Ready to Start Your Next Project?
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            Let's discuss how Crow Corporation can bring precision and
            excellence to your manufacturing needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-700 rounded-lg hover:bg-gray-100 font-semibold transition-all shadow-lg"
              data-testid="cta-contact-button"
            >
              Contact Us Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <a
              href="tel:+12813574600"
              className="inline-flex items-center justify-center px-8 py-4 bg-blue-800 text-white rounded-lg hover:bg-blue-900 font-semibold transition-all border-2 border-white/20"
              data-testid="cta-phone-button"
            >
              Call: 281.357.4600
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
